import { debounce, isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities'

class CollectionDescriptionExpand extends HTMLElement {
  #debouncedUpdate = debounce(() => {
    this.update()
  }, 100)

  connectedCallback() {
    const textEl = this.querySelector('.text-block')
    const buttonEl = this.querySelector('.custom-collection-description__button')
    if (!(textEl instanceof HTMLElement) || !(buttonEl instanceof HTMLElement)) return

    this.text = textEl
    this.button = buttonEl

    if (!this.text.id) {
      this.text.id = `collection-description-${Math.random().toString(36).slice(2)}`
    }

    this.button.setAttribute('aria-controls', this.text.id)
    this.button.addEventListener('click', this.toggle)
    window.addEventListener('resize', this.#debouncedUpdate)
    mediaQueryLarge.addEventListener('change', this.update)
    this.update()
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.toggle)
    window.removeEventListener('resize', this.#debouncedUpdate)
    mediaQueryLarge.removeEventListener('change', this.update)
    this.#debouncedUpdate.cancel()
  }

  /**
   * @param {boolean} expanded
   */
  #setButtonLabel(expanded) {
    const label = this.button?.querySelector('.custom-collection-description__button-label')
    if (!label || !this.button) return

    label.textContent = expanded
      ? (this.button.dataset.labelLess ?? '')
      : (this.button.dataset.labelMore ?? '')
  }

  toggle = () => {
    const { button } = this
    if (!button) return

    const expanded = this.dataset.expanded !== 'true'
    this.dataset.expanded = expanded ? 'true' : 'false'
    button.setAttribute('aria-expanded', String(expanded))
    this.#setButtonLabel(expanded)
  }

  /**
   * Compare natural text height against the mobile clamp threshold.
   * Must not measure while line-clamp is active — clamped text reports no overflow.
   * @param {HTMLElement} text
   */
  #measureOverflow(text) {
    this.dataset.overflow = 'false'
    void text.offsetHeight
    const naturalHeight = text.scrollHeight

    this.dataset.overflow = 'pending'
    void text.offsetHeight
    const clampedHeight = text.clientHeight

    return naturalHeight - clampedHeight > 1
  }

  update = () => {
    const { button, text } = this
    if (!button || !text) return

    const isMobile = isMobileBreakpoint()

    if (!isMobile) {
      this.dataset.overflow = 'false'
      button.hidden = true
      return
    }

    if (this.dataset.expanded === 'true') {
      this.dataset.overflow = 'true'
      button.hidden = false
      button.setAttribute('aria-expanded', 'true')
      this.#setButtonLabel(true)
      return
    }

    const hasOverflow = this.#measureOverflow(text)
    this.dataset.overflow = hasOverflow ? 'true' : 'false'
    button.hidden = !hasOverflow
    button.setAttribute('aria-expanded', 'false')
    this.#setButtonLabel(false)
  }
}

if (!customElements.get('collection-description-expand')) {
  customElements.define('collection-description-expand', CollectionDescriptionExpand)
}
